import { getDefaultLocation } from './location.helper';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { geoDebug } from './debug-geo';

/**
 * The reverse geocode only calls back on success (and not at all when Google
 * Maps hasn't loaded), so a pending "Nearby" would hang forever on a failure.
 * Fall back to the raw coordinates after this long — they're all the search
 * actually needs.
 */
const GEOCODE_TIMEOUT_MS = 6000;

/** Coords-only record — enough to search, used when the geocode doesn't answer. */
function coordsOnlyResult(lat: number, lng: number): GeoCodeResult {
    return {
        address_components:
            [] as unknown as GeoCodeResult['address_components'],
        formatted_address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        geometry: {
            location: { lat, lng },
            location_type: 'APPROXIMATE'
        },
        place_id: '',
        plus_code: { compound_code: '', global_code: '' },
        types: [] as unknown as GeoCodeResult['types'],
        lat,
        log: lng,
        source: 'user-geolocation'
    };
}

/**
 * Resolve the visitor's current position into a GeoCodeResult, so "Nearby" can
 * travel the same path as any other picked place (record it, then open explore
 * on it) instead of being a special case.
 *
 * `onUnavailable` runs when geolocation is missing, not granted, or refused —
 * callers pass their own permission prompt, which is page-specific.
 */
export function resolveNearbyLocation(
    onResolved: (result: GeoCodeResult) => void,
    onUnavailable: () => void
): void {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        geoDebug('resolveNearbyLocation: geolocation unavailable');
        onUnavailable();
        return;
    }

    const useCoords = (coords: GeolocationCoordinates) => {
        geoDebug('resolveNearbyLocation: got coords', {
            lat: coords.latitude,
            lng: coords.longitude
        });
        let settled = false;
        // Only ever read asynchronously (the geocode callback and the timeout
        // both fire in a later task), so referring to it here is safe.
        const settle = (result: GeoCodeResult) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            // Keep the device's own coordinates — the geocoded result carries
            // the address, not necessarily the exact point we searched from.
            result.lat = coords.latitude;
            result.log = coords.longitude;
            result.source = 'user-geolocation';
            onResolved(result);
        };
        const timer = setTimeout(() => {
            geoDebug('resolveNearbyLocation: geocode timed out, using coords');
            settle(coordsOnlyResult(coords.latitude, coords.longitude));
        }, GEOCODE_TIMEOUT_MS);
        getDefaultLocation(coords.latitude, coords.longitude, settle);
    };

    const request = () =>
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => useCoords(coords),
            () => {
                geoDebug('resolveNearbyLocation: position request refused');
                onUnavailable();
            }
        );

    if (!('permissions' in navigator)) {
        request();
        return;
    }

    navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
            geoDebug('resolveNearbyLocation: permission state', status.state);
            if (status.state === 'granted') request();
            else onUnavailable();
        })
        .catch(() => request());
}
